import { forwardRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { useT } from "../i18n/LanguageContext";
import { EXPERIENCE_VALUES } from "../i18n/translations";

const LeadForm = forwardRef(function LeadForm({ defaultExperience }, ref) {
    const { t } = useT();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        investment_intent: "",
        preferred_experience: defaultExperience || "",
        budget: "",
        timeline: "",
        nationality: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const submit = async (e) => {
        e.preventDefault();
        if (
            !form.name ||
            !form.email ||
            !form.phone ||
            !form.investment_intent ||
            !form.preferred_experience
        ) {
            toast.error(t("form.required_error"));
            return;
        }
        setLoading(true);
        try {
            const payload = { ...form };
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") payload[k] = null;
            });
            await api.post("/leads", payload);
            setDone(true);
            toast.success(t("form.success_received"));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const experienceOptions = [
        {
            id: "inperson",
            value: EXPERIENCE_VALUES.inperson,
            title: t("form.experience.inperson_title"),
            sub: t("form.experience.inperson_sub"),
        },
        {
            id: "video",
            value: EXPERIENCE_VALUES.video,
            title: t("form.experience.video_title"),
            sub: t("form.experience.video_sub"),
        },
        {
            id: "vr",
            value: EXPERIENCE_VALUES.vr,
            title: t("form.experience.vr_title"),
            sub: t("form.experience.vr_sub"),
        },
    ];

    return (
        <section
            id="contact"
            ref={ref}
            className="relative py-24 md:py-32 bg-charcoal"
            data-testid="lead-form-section"
        >
            <div className="mx-auto max-w-5xl px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="text-center"
                >
                    <div className="flex items-center justify-center gap-3 mb-5">
                        <span className="inline-block w-8 h-[1px] bg-gold" />
                        <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-semibold">
                            {t("form.overline")}
                        </span>
                        <span className="inline-block w-8 h-[1px] bg-gold" />
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
                        {t("form.heading")}
                    </h2>
                    <p className="mt-5 text-white/60 max-w-xl mx-auto">
                        {t("form.subhead")}
                    </p>
                </motion.div>

                {done ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-14 p-10 md:p-16 border border-gold/40 bg-gold/5 text-center"
                        data-testid="lead-form-success"
                    >
                        <CheckCircle2
                            className="mx-auto text-gold"
                            size={40}
                            strokeWidth={1.2}
                        />
                        <h3 className="mt-5 font-display text-2xl md:text-3xl">
                            {t("form.success_heading")}
                        </h3>
                        <p className="mt-3 text-white/70 max-w-md mx-auto">
                            {t("form.success_body")}
                        </p>
                    </motion.div>
                ) : (
                    <form
                        onSubmit={submit}
                        className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5"
                        data-testid="lead-form"
                    >
                        <Field label={t("form.name")}>
                            <Input
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                placeholder={t("form.name_ph")}
                                className="bg-transparent border-white/20 h-12 rounded-none focus-visible:ring-gold focus-visible:border-gold"
                                data-testid="lead-name"
                                required
                            />
                        </Field>

                        <Field label={t("form.email")}>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                placeholder={t("form.email_ph")}
                                className="bg-transparent border-white/20 h-12 rounded-none focus-visible:ring-gold focus-visible:border-gold"
                                data-testid="lead-email"
                                required
                            />
                        </Field>

                        <Field label={t("form.phone")}>
                            <Input
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                placeholder={t("form.phone_ph")}
                                className="bg-transparent border-white/20 h-12 rounded-none focus-visible:ring-gold focus-visible:border-gold"
                                data-testid="lead-phone"
                                required
                            />
                        </Field>

                        <Field label={t("form.nationality")}>
                            <Input
                                value={form.nationality}
                                onChange={(e) => update("nationality", e.target.value)}
                                placeholder={t("form.nationality_ph")}
                                className="bg-transparent border-white/20 h-12 rounded-none focus-visible:ring-gold focus-visible:border-gold"
                                data-testid="lead-nationality"
                            />
                        </Field>

                        <Field label={t("form.intent")}>
                            <Select
                                value={form.investment_intent}
                                onValueChange={(v) => update("investment_intent", v)}
                            >
                                <SelectTrigger
                                    className="bg-transparent border-white/20 h-12 rounded-none focus:ring-gold"
                                    data-testid="lead-intent"
                                >
                                    <SelectValue placeholder={t("form.intent_ph")} />
                                </SelectTrigger>
                                <SelectContent className="bg-charcoal-surface border-white/20 text-white">
                                    <SelectItem value="Personal use">{t("form.intent.personal")}</SelectItem>
                                    <SelectItem value="Investment">{t("form.intent.investment")}</SelectItem>
                                    <SelectItem value="Both">{t("form.intent.both")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label={t("form.budget")}>
                            <Select
                                value={form.budget}
                                onValueChange={(v) => update("budget", v)}
                            >
                                <SelectTrigger
                                    className="bg-transparent border-white/20 h-12 rounded-none focus:ring-gold"
                                    data-testid="lead-budget"
                                >
                                    <SelectValue placeholder={t("form.budget_ph")} />
                                </SelectTrigger>
                                <SelectContent className="bg-charcoal-surface border-white/20 text-white">
                                    <SelectItem value="Under $500K">{t("form.budget.o1")}</SelectItem>
                                    <SelectItem value="$500K – $1M">{t("form.budget.o2")}</SelectItem>
                                    <SelectItem value="$1M – $2M">{t("form.budget.o3")}</SelectItem>
                                    <SelectItem value="$2M+">{t("form.budget.o4")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label={t("form.timeline")}>
                            <Select
                                value={form.timeline}
                                onValueChange={(v) => update("timeline", v)}
                            >
                                <SelectTrigger
                                    className="bg-transparent border-white/20 h-12 rounded-none focus:ring-gold"
                                    data-testid="lead-timeline"
                                >
                                    <SelectValue placeholder={t("form.timeline_ph")} />
                                </SelectTrigger>
                                <SelectContent className="bg-charcoal-surface border-white/20 text-white">
                                    <SelectItem value="Immediately">{t("form.timeline.o1")}</SelectItem>
                                    <SelectItem value="1–3 months">{t("form.timeline.o2")}</SelectItem>
                                    <SelectItem value="3–6 months">{t("form.timeline.o3")}</SelectItem>
                                    <SelectItem value="Exploring">{t("form.timeline.o4")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>

                        <div className="md:col-span-2">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-4">
                                {t("form.experience")}
                            </div>
                            <RadioGroup
                                value={form.preferred_experience}
                                onValueChange={(v) => update("preferred_experience", v)}
                                className="grid grid-cols-1 md:grid-cols-3 gap-4"
                            >
                                {experienceOptions.map((opt) => (
                                    <label
                                        key={opt.id}
                                        htmlFor={`exp-${opt.id}`}
                                        className={`cursor-pointer p-5 border transition-colors flex items-start gap-4 ${
                                            form.preferred_experience === opt.value
                                                ? "border-gold bg-gold/5"
                                                : "border-white/15 hover:border-gold"
                                        }`}
                                    >
                                        <RadioGroupItem
                                            id={`exp-${opt.id}`}
                                            value={opt.value}
                                            className="border-white/40 text-gold mt-1"
                                            data-testid={`lead-exp-${opt.id}`}
                                        />
                                        <div>
                                            <div className="font-display text-base md:text-lg">{opt.title}</div>
                                            <div className="text-xs text-white/50 mt-1">{opt.sub}</div>
                                        </div>
                                    </label>
                                ))}
                            </RadioGroup>
                        </div>

                        <div className="md:col-span-2 flex flex-col sm:flex-row items-center justify-between gap-5 mt-4">
                            <div className="text-xs text-white/40 italic text-center sm:text-left">
                                {t("form.disclaimer")}
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gold text-charcoal px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-gold-hover transition-colors disabled:opacity-60"
                                data-testid="lead-submit-btn"
                            >
                                {loading ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Send size={14} />
                                )}
                                {t("form.submit")}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </section>
    );
});

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3 block">
                {label}
            </Label>
            {children}
        </div>
    );
}

export default LeadForm;
